import { FC, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import {
  institutionalAuthConfig,
  accountAuthConfig,
  clearOktaAuths,
} from "@repo/auth-config";
import {
  getOktaAccountAuthObject,
  getOktaAuthObject,
  getSessionAndTokens,
} from "@repo/auth-config";
import { AudienceContext } from "@repo/contexts";
import { buildQueryString, getUserAudienceBasePath } from "@repo/utils";
import { Loader } from "@repo/ui";
import { ACC_TOKEN, Routes } from "../constants";
import { ExtendedAccessToken } from "../interfaces";
import { TokenResponse } from "@okta/okta-auth-js";
import { Outlet, matchPath } from "react-router-dom";

const advisorGroupNames = ["Advisor", "Advisors"];

const getLoggedinRedirectUrl = (userAudienceBasePath: string) => {
  const params = ["next", "region"];
  const queryParams = buildQueryString(params);
  const redirectUrl = `${userAudienceBasePath}${
    queryParams ? "?" + queryParams : ""
  }`;

  if (import.meta.env.DEV) {
    console.log(`Cannot redirect to ${redirectUrl} on localhost`);
    return "";
  }

  return encodeURI(redirectUrl);
};

const checkIsAdvisor = (token: ExtendedAccessToken) => {
  return advisorGroupNames.some((groupName) =>
    token?.claims?.groups?.includes(groupName),
  );
};

const clearOktaAuthsWithLogging = async () => {
  try {
    await clearOktaAuths();
  } catch (error) {
    console.error("Error clearing okta auth session: ", error);
  }
};

const RedirectToInstitution: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const audienceCxt = useContext(AudienceContext);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const handleRedirect = (url: string | undefined) => {
      if (!isMounted || !url) return;
      window.location.replace(url);
    };

    const checkAuthenticationStatus = async () => {
      try {
        // ignore logout redirects from legacy app
        const isLogout =
          Boolean(
            matchPath(
              { path: Routes.LOGOUT_EUA, end: true },
              window.location.pathname,
            ),
          ) ||
          Boolean(
            matchPath(
              { path: Routes.LOGOUT_NA, end: true },
              window.location.pathname,
            ),
          );
        if (isLogout) {
          return;
        }

        const institutionalAuth = getOktaAuthObject(institutionalAuthConfig);
        const accountAuth = getOktaAccountAuthObject(accountAuthConfig);
        const redirectBasePath = getUserAudienceBasePath(audienceCxt);
        const redirectUrl = getLoggedinRedirectUrl(redirectBasePath);

        // Check for existing tokens
        try {
          const [institutionalToken, accountToken] = await Promise.all([
            institutionalAuth.tokenManager.get(ACC_TOKEN),
            accountAuth.tokenManager.get(ACC_TOKEN),
          ]);
          if (institutionalToken && accountToken) {
            handleRedirect(redirectUrl);
            return;
          }
        } catch (error) {
          console.warn("Unable to get existing tokens: ", error);
        }

        let tokenResponse: TokenResponse | undefined;
        let accessToken: ExtendedAccessToken | undefined;

        // Try to get user tokens from session
        try {
          tokenResponse = await getSessionAndTokens(institutionalAuth);
          accessToken = tokenResponse?.tokens
            .accessToken as ExtendedAccessToken;
        } catch (error) {
          console.error("Unable to get user token: ", error);
          await clearOktaAuthsWithLogging();
          return;
        }

        // If no token response, user is not authenticated
        if (!tokenResponse) {
          console.log("No token response received");
          await clearOktaAuthsWithLogging();
          return;
        }

        const isAdvisor = accessToken && checkIsAdvisor(accessToken);
        // Let through advisor users
        if (isAdvisor) {
          handleRedirect(redirectUrl);
          return;
        }

        // Check for legacy session cookie
        const instCookie = Cookies.get("inst-nc");
        if (!instCookie) {
          console.log("Legacy session cookie not found");
          await clearOktaAuthsWithLogging();
          return;
        }

        // Process tokenResponse for account tokens
        if (isMounted) {
          institutionalAuth.tokenManager.setTokens(tokenResponse.tokens);
        }
        try {
          const accountTokens = await getSessionAndTokens(accountAuth);
          if (accountTokens && redirectUrl) {
            if (isMounted) {
              await accountAuth.handleLoginRedirect(
                accountTokens.tokens,
                redirectUrl,
              );
            }
            return;
          }
          console.log("Unable to obtain account token");
          await clearOktaAuthsWithLogging();
          return;
        } catch (error) {
          console.error("Error getting account token: ", error);
          await clearOktaAuthsWithLogging();
          return;
        }
      } catch (error) {
        console.error("Unexpected error in authentication flow: ", error);
        await clearOktaAuthsWithLogging();
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    checkAuthenticationStatus();
    return () => {
      isMounted = false;
    };
  }, [audienceCxt]);

  return (
    <>
      {isLoading && <Loader isFullPage />}
      <Outlet />
    </>
  );
};

export default RedirectToInstitution;
